import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress
} from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import axios from "axios";
import api from "../common/api";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const normalizeList = payload => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

const normalizeProcesses = payload => {
  const candidates = [
    payload,
    payload && payload.invoicing_schedules_commands,
    payload && payload.invoicing_schedule_commands,
    payload && payload.commands,
    payload && payload.processes
  ];

  for (const candidate of candidates) {
    const list = normalizeList(candidate);
    if (list.length > 0) return list;
  }

  return [];
};

export default function InvoicingSchedule() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [billingPeriod, setBillingPeriod] = useState({
    name: "",
    start_date: "",
    end_date: ""
  });
  const [commands, setCommands] = useState([]);
  const [commandValues, setCommandValues] = useState({});
  const [commandDateValues, setCommandDateValues] = useState({});
  const [loadingCommands, setLoadingCommands] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackBar, setSnackBar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const token = localStorage.getItem("userToken");

  useEffect(() => {
    const load = async () => {
      setLoadingCommands(true);
      try {
        const commandsResponse = await axios.get(api.invoicing_schedules_commands, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = normalizeProcesses(commandsResponse.data);

        setCommands(list);

        const initial = {};
        list.forEach(cmd => {
          initial[cmd.id] = "";
        });
        setCommandValues(initial);
      } catch (err) {
        console.error(err);
        setCommands([]);
        setSnackBar({
          open: true,
          severity: "error",
          message: "No se pudieron cargar los procesos"
        });
      } finally {
        setLoadingCommands(false);
      }
    };
    load();
  }, []);

  const handleCommandChange = (idx, value) => {
    setCommandValues(prev => ({ ...prev, [idx]: value }));
  };

  const handleCommandChangeDate = (idx, value) => {
    setCommandDateValues(prev => ({ ...prev, [idx]: value }));
  };

  const allCommandsFilled = () => {
    if (commands.length === 0) return false;
    return commands.every((cmd, index) => (commandValues[index] || "").trim() !== "");
  };

  const handleSubmit = async (e) => {
    if (!name.trim()) {
      setSnackBar({
        open: true,
        severity: "error",
        message: "Ingrese un nombre"
      });
      return;
    }
    if (!allCommandsFilled()) {
      setSnackBar({
        open: true,
        severity: "error",
        message: "Debe completar todos los procesos"
      });
      return;
    }
    if (!billingPeriod.start_date || !billingPeriod.end_date) {
      setSnackBar({
        open: true,
        severity: "error",
        message: "Ingrese fecha inicio y fin del periodo de facturación"
      });
      return;
    }

    setSaving(true);
    const formData = new FormData();
    formData.append("invoicing_schedule[name]", name.trim());
    formData.append("invoicing_schedule[description]", description.trim());
    formData.append("billing_period[name]", name.trim());
    formData.append("billing_period[start_date]", billingPeriod.start_date);
    formData.append("billing_period[end_date]", billingPeriod.end_date);

    commands.forEach((cmd, index) => {
        formData.append(
            `invoice_schedules[${index}][name]`,
            (commandValues[index] || "").trim()
          );
          formData.append(
        `invoice_schedules[${index}][description]`,
        (commandValues[index] || "").trim()
      );
      formData.append(
        `invoice_schedules[${index}][scheduled_date]`,
        (commandDateValues[index] || "").trim()
      );
      formData.append(
        `invoice_schedules[${index}][command]`,
        cmd.id
      );
    });

    try {
      const response = await axios.post(api.invoicing_schedules, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });
      if (response.status === 200 || response.status === 201) {
        window.location.href = "/invoicing-schedules";
      }
    } catch (error) {
      let error_message = "Error al guardar";
      if (error.response && error.response.data) {
        const d = error.response.data;
        if (d.errors) {
          for (const key in d.errors) {
            error_message += ` ${d.errors[key]}`;
          }
        } else if (d.message) {
          error_message = d.message;
        }
      }
      console.error(error);
      setSnackBar({
        open: true,
        severity: "error",
        message: error_message
      });
    }
    setSaving(false);
  };

  const handleClose = () => {
    window.location.href = "/invoicing-schedules";
  };

  if (loadingCommands) {
    return (
      <Box display="flex" alignItems="center" p={2}>
        <CircularProgress size={28} style={{ marginRight: 12 }} />
        <Typography variant="body1">Cargando procesos...</Typography>
      </Box>
    );
  }

  return (
    <div>
      <Snackbar
        open={snackBar.open}
        onClose={() => setSnackBar({ ...snackBar, open: false })}
      >
        <Alert
          onClose={() => setSnackBar({ ...snackBar, open: false })}
          severity={snackBar.severity}
        >
          {snackBar.message}
        </Alert>
      </Snackbar>

      <div className="row mb-4">
        <div className="col-12">
          <Typography variant="h6">Nueva programación de facturación</Typography>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <TextField
            fullWidth
            required
            label="Nombre"
            value={name}
            onChange={e => setName(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { fontSize: "0.75rem" } }}
          />
        </div>
        <div className="col-md-8">
          <TextField
            fullWidth
            label="Descripción"
            value={description}
            onChange={e => setDescription(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { fontSize: "0.75rem" } }}
          />
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-md-4">
          <TextField
            fullWidth
            label="Fecha inicio periodo"
            type="date"
            value={billingPeriod.start_date}
            onChange={e => {
              const value = e.target.value;
              setBillingPeriod(prev => ({ ...prev, start_date: value }));
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { fontSize: "0.75rem" } }}
          />
        </div>
        <div className="col-md-4">
          <TextField
            fullWidth
            label="Fecha fin periodo"
            type="date"
            value={billingPeriod.end_date}
            onChange={e => {
              const value = e.target.value;
              setBillingPeriod(prev => ({ ...prev, end_date: value }));
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { fontSize: "0.75rem" } }}
          />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <Typography variant="subtitle2" gutterBottom>
            Procesos (todos obligatorios)
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    Proceso
                  </TableCell>
                  <TableCell style={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    Descripción
                  </TableCell>
                  <TableCell style={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    Fecha de ejecución
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commands.map((cmd, index) => (
                  <TableRow key={index}>
                    <TableCell
                      style={{ fontSize: "0.75rem", width: "28%" }}
                      component="th"
                      scope="row"
                    >
                      {cmd.name}
                    </TableCell>
                    <TableCell style={{ fontSize: "0.75rem" }}>
                      <TextField
                        fullWidth
                        required
                        value={commandValues[index] || ""}
                        onChange={e =>
                          handleCommandChange(index, e.target.value)
                        }
                        placeholder="Completar"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { fontSize: "0.75rem" } }}
                      />
                    </TableCell>
                    <TableCell style={{ fontSize: "0.75rem" }}>
                      <TextField
                        fullWidth
                        type="date"
                        value={commandDateValues[index] || ""}
                        onChange={e => handleCommandChangeDate(index, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { fontSize: "0.75rem" } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {commands.length === 0 && (
            <Typography variant="body2" color="textSecondary" className="mt-2">
              No hay procesos configurados en el sistema.
            </Typography>
          )}
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <Button onClick={handleClose} color="secondary" size="small">
            Cancelar
          </Button>{" "}
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            size="small"
            disabled={saving || commands.length === 0}
          >
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
}
